import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { userLogin } from '@/api/service/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppContext } from '@/contexts/AppContext';
import { getApiErrorMessage } from '@/api/errorHandler';
import {
  AlertCircle,
  CreditCard,
  Eye,
  EyeOff,
  Lock,
  ShieldCheck,
  TrendingUp,
  User,
  Users,
} from 'lucide-react';

const LOGO_URL =
  'https://media.istockphoto.com/id/1423550966/vector/profit-rounded-lines-icon.jpg?s=612x612&w=0&k=20&c=_KFEK2PUIlquKGVUYQ18I2rO6xQ3ieFDEx-xHpXRLTI=';

const FIELD_CLASS =
  'h-11 rounded-lg border-gray-200 bg-white pl-10 shadow-xs transition-colors focus-visible:border-[#4369a0] focus-visible:ring-2 focus-visible:ring-[#4369a0]/20';

const HIGHLIGHTS = [
  { icon: Users, label: 'Members', tone: 'bg-[#4369a0]/10 text-[#4369a0]' },
  { icon: CreditCard, label: 'Payments', tone: 'bg-teal-50 text-teal-700' },
  { icon: TrendingUp, label: 'Insights', tone: 'bg-emerald-50 text-emerald-700' },
] as const;

function LoginBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(145deg,#d4e0ee_0%,#eaf0f7_38%,#f4f7fb_62%,#dce8f4_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(67,105,160,0.22)_0%,transparent_42%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_75%,rgba(13,148,136,0.16)_0%,transparent_40%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.85)_0%,transparent_55%)]" />

      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
      >
        <defs>
          <linearGradient id="login-line" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4369a0" stopOpacity="0" />
            <stop offset="50%" stopColor="#4369a0" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#4369a0" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="login-area" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#4369a0" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#4369a0" stopOpacity="0" />
          </linearGradient>
        </defs>

        <circle cx="180" cy="160" r="130" fill="none" stroke="#4369a0" strokeOpacity="0.1" strokeWidth="1.5" />
        <circle cx="180" cy="160" r="190" fill="none" stroke="#4369a0" strokeOpacity="0.06" strokeWidth="1" />
        <circle cx="180" cy="160" r="250" fill="none" stroke="#4369a0" strokeOpacity="0.04" strokeWidth="1" />

        <circle cx="1260" cy="720" r="150" fill="none" stroke="#0d9488" strokeOpacity="0.12" strokeWidth="1.5" />
        <circle cx="1260" cy="720" r="220" fill="none" stroke="#0d9488" strokeOpacity="0.07" strokeWidth="1" />
        <circle cx="1260" cy="720" r="290" fill="none" stroke="#0d9488" strokeOpacity="0.04" strokeWidth="1" />

        <path
          d="M0 620 C 240 540, 420 760, 720 680 C 980 610, 1180 520, 1440 600 L 1440 900 L 0 900 Z"
          fill="url(#login-area)"
        />
        <path
          d="M0 500 C 280 420, 520 560, 820 480 C 1080 410, 1240 350, 1440 420"
          fill="none"
          stroke="url(#login-line)"
          strokeWidth="2"
        />
        <path
          d="M0 560 C 220 500, 460 620, 760 550 C 1020 490, 1200 430, 1440 500"
          fill="none"
          stroke="#0d9488"
          strokeOpacity="0.15"
          strokeWidth="1.5"
          strokeDasharray="8 10"
        />

        {[...Array(12)].map((_, i) => (
          <circle
            key={i}
            cx={120 + i * 110}
            cy={780 - (i % 3) * 28}
            r="3"
            fill="#4369a0"
            fillOpacity={0.12 + (i % 4) * 0.04}
          />
        ))}
      </svg>

      <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-[#4369a0]/20 blur-[90px]" />
      <div className="absolute -right-20 bottom-12 h-80 w-80 rounded-full bg-[#0d9488]/15 blur-[100px]" />
      <div className="absolute left-1/2 top-8 h-56 w-[640px] -translate-x-1/2 rounded-full bg-white/50 blur-[80px]" />

      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(67, 105, 160, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(67, 105, 160, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
        }}
      />

      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(45,79,115,0.08)_100%)]" />
    </div>
  );
}

export default function Login() {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { setIsAuthenticated } = useAppContext();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!userId || !password) {
      setError('Please enter both User ID and Password');
      return;
    }

    setIsLoading(true);

    try {
      const data = await userLogin({ username: userId, password });
      localStorage.setItem('accessToken', data.access_token);
      setIsAuthenticated(true);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error(error);
      setError(getApiErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-10">
      <LoginBackground />

      <div className="relative z-10 w-full max-w-[460px]">
        <div className="overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-[0_20px_60px_-12px_rgba(67,105,160,0.22)]">
          {/* Brand header */}
          <div className="relative overflow-hidden bg-gradient-to-br from-[#4369a0] via-[#3a5d8f] to-[#2d4f73] px-6 pb-12 pt-6 text-center text-white">
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.14]"
              style={{
                backgroundImage: `
                  linear-gradient(135deg, rgba(255,255,255,0.18) 25%, transparent 25%),
                  linear-gradient(225deg, rgba(255,255,255,0.18) 25%, transparent 25%),
                  linear-gradient(45deg, rgba(255,255,255,0.18) 25%, transparent 25%),
                  linear-gradient(315deg, rgba(255,255,255,0.18) 25%, transparent 25%)
                `,
                backgroundSize: '20px 20px',
                backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0',
              }}
            />
            <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-[#0d9488]/20 blur-3xl" />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/20" />

            <div className="relative z-10">
              <div className="mx-auto mb-5 w-fit">
                <div className="rounded-full bg-white/15 p-1 ring-2 ring-white/25 backdrop-blur-sm">
                  <div className="rounded-full bg-white p-1 shadow-lg">
                    <img
                      src={LOGO_URL}
                      alt="PMS Logo"
                      className="h-[68px] w-[68px] rounded-full border border-gray-100 object-cover"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-3">
                {/* <span className="h-px w-10 bg-gradient-to-r from-transparent to-white/50" /> */}
                <h1 className="text-3xl font-bold tracking-[0.20em]">PMS</h1>
                {/* <span className="h-px w-10 bg-gradient-to-l from-transparent to-white/50" /> */}
              </div>

              <div className="mx-auto mt-3 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 backdrop-blur-sm">
                <span className="h-1 w-1 rounded-full bg-teal-300" />
                <p className="text-base leading-relaxed text-white/90 sm:text-lg">
                  பொருளாதார முன்னேற்ற சங்கம்
                </p>
                <span className="h-1 w-1 rounded-full bg-teal-300" />
              </div>
            </div>

            <svg
              className="absolute -bottom-px left-0 block w-full"
              viewBox="0 0 460 40"
              preserveAspectRatio="none"
              aria-hidden
            >
              <path
                d="M0,18 C80,36 160,0 230,18 C300,36 380,0 460,18 L460,40 L0,40 Z"
                fill="#ffffff"
                stroke="none"
              />
            </svg>
          </div>

          {/* Form */}
          <div className="px-6 pb-5 pt-0">
            <div className="mb-6 text-center">
              <h2 className="text-lg font-semibold text-gray-900">Welcome back</h2>
              <div className="mt-3 flex items-center justify-center gap-3">
                <span className="h-px w-12 bg-gradient-to-r from-transparent via-[#4369a0]/25 to-[#4369a0]/40" />
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#4369a0]/20 bg-gradient-to-r from-[#4369a0]/8 to-[#4369a0]/4 px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#4369a0] shadow-xs">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  ADMIN PORTAL
                </span>
                <span className="h-px w-12 bg-gradient-to-l from-transparent via-[#4369a0]/25 to-[#4369a0]/40" />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="userId" className="text-sm font-medium text-gray-700">
                  User ID
                </Label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="userId"
                    type="text"
                    placeholder="Enter your user ID"
                    value={userId}
                    onChange={(e) => {
                      setUserId(e.target.value);
                      setError('');
                    }}
                    className={FIELD_CLASS}
                    disabled={isLoading}
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                    className={`${FIELD_CLASS} pr-11`}
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                className="mt-1 h-11 w-full rounded-lg bg-gradient-to-r from-[#4369a0] to-[#365a8c] text-base font-medium text-white shadow-md shadow-[#4369a0]/25 transition-all hover:from-[#3a5d8f] hover:to-[#2d4f73] hover:shadow-lg hover:shadow-[#4369a0]/30"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>

            <div className="mt-7 grid grid-cols-3 gap-2.5">
              {HIGHLIGHTS.map(({ icon: Icon, label, tone }) => (
                <div
                  key={label}
                  className="flex flex-col items-center gap-2 rounded-xl border border-gray-100 bg-gray-50/70 px-2 py-3 text-center"
                >
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <span className="text-xs font-medium text-gray-600">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 border-t border-gray-100 bg-gray-50/80 px-6 py-3.5 text-xs text-gray-500">
            <ShieldCheck className="h-3.5 w-3.5 text-[#4369a0]" />
            <span>Secure access for authorized administrators only</span>
          </div>
        </div>
      </div>
    </div>
  );
}
